import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import axios from 'axios';
import AppleIcon from 'icons/Apple.icon';
import FacebookIcon from 'icons/Facebook.icon';
import GoogleIcon from 'icons/Google.icon';
import ChevronDownIcon from 'icons/ChevronDown.icon';
import AuthCard from 'features/Auth/AuthCard/AuthCard.wrapper';
import { Link, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { signIn } from '@utils/auth';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import authStore from 'store/authStore';
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google';

const StyledLink = styled(Link)(() => ({
  fontWeight: 500,
  fontSize: '11px',
  lineHeight: '13.42px',
  color: 'var(--Interviewer-Lighter-Black)',
  textDecoration: 'none',

  '&:hover': {
    color: 'var(--Interviewer-Black)',
  },
}));

const ForgotPasswordLink = styled(Link)(() => ({
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '17px',
  color: 'var(--Interviewer-Green)',
  textDecoration: 'none',
  marginLeft: 'auto',
  cursor: 'pointer',

}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '12px',
  padding: '18px 48px',
  backgroundColor: '#f2f2f2',
  width: '100%',

  [theme.breakpoints.up('md')]: {
    width: 'fit-content',
  },
}));


const Login = () => {
  const navigate = useNavigate();
  // const login = useAuthStore((s) => s.login)

  const {authData, updateAuthData} = authStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const response = await axios.post('https://interviewer.runasp.net/api/users/login', credentials);
      return response.data;
    },
    onSuccess: (data) => {

      // Smestanje podataka u store
      updateAuthData({
        token: data.token,
        refreshToken: data.refreshToken,
        roles: data.roles
      });
      // login(data.token, data.roles);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken)
      signIn();
      navigate({ to: '/technologies' });
    },
    onError: (error) => {
      console.error('Login error:', error);
    },
  });

  const handleLogin = () => {
    loginMutation.mutate({ email, password });
  };


  const googleLogin = useGoogleLogin({
    onSuccess: (response) => {
      console.log(response);
    },onError: () => {
      console.log('Failed login');
    }
  })

  return (
    <AuthCard>
      <Box>
        <Typography variant="h1">Log in</Typography>
        <Typography variant="h4">Continue to Interviewer</Typography>
      </Box>

      <TextField 
        label="Enter your email here" 
        variant="outlined" 
        fullWidth 
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setShowEmailError(false);
        }}
        error={!!loginMutation.error || showEmailError}
        helperText={
          loginMutation.error 
            ? 'Invalid email' 
            : showEmailError 
              ? 'Please enter your email to reset password' 
              : ''
        }
        disabled={loginMutation.isPending}
      />

      <TextField 
        label="Enter your password here"
        type={showPassword ? 'text' : 'password'}
        value={password}
        variant="outlined" 
        fullWidth 
        onChange={(e) => setPassword(e.target.value)}
        slotProps={{
          input: {
            endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
          }
        }}
        error={!!loginMutation.error}
        helperText={loginMutation.error ? 'Invalid password' : ''}
        disabled={loginMutation.isPending}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        {/* <FormControlLabel
          control={<Checkbox defaultChecked />}
          label="Remember me"
        /> */}
        <ForgotPasswordLink to="/forgot-password">
          Forgot password?
        </ForgotPasswordLink>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleLogin}
        fullWidth
        disabled={loginMutation.isPending}
      >
        {loginMutation.isPending ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Login'
        )}
      </Button>

      <Divider>or</Divider>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <StyledIconButton onClick={() => googleLogin()}>
          <GoogleIcon />
        </StyledIconButton>
        <StyledIconButton>
          <FacebookIcon />
        </StyledIconButton>
        <StyledIconButton>
          <AppleIcon />
        </StyledIconButton>
      </Box>

      <Typography>
        New to Interviewer?
        <Link
          to="/register"
          style={{
            marginLeft: '5px',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
            color: 'var(--Interviewer-Green)',
            textDecoration: 'none',
          }}
        >
          Sign up here
        </Link>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          marginTop: 2,
          gap: 1,
        }}
      >
        <StyledLink href="#" color="text.secondary">
          Help
        </StyledLink>
        <StyledLink href="#" color="text.secondary">
          Terms
        </StyledLink>
        <StyledLink href="#" color="text.secondary">
          Privacy
        </StyledLink>
      </Box>
    </AuthCard>
  );
};

export default Login;
